<template>
  <div
    class="app-container"
    :class="{ 'app-container--full-bleed': isFullBleed }"
  >
    <recommended-browser-banner />
    <div
      v-if="!isAuthPage"
      class="content"
    >
      <app-layout>
        <template #appSidebar>
          <app-sidebar
            class="menu"
            :sidebar-width="sidebarWidth"
            @mouseenter="handleToggleWidth"
            @mouseleave="handleToggleWidth"
          />
        </template>
        <template #appMainContent>
          <main class="main">
            <branding-header @on-sidebar-open="handleSidebarOpen">
              <template #title-bar>
                <!-- Routed views can occupy the title bar; the document does -->
                <div
                  id="app-title-bar-slot"
                  class="app-title-bar-slot"
                />
              </template>
            </branding-header>
            <div class="s-row">
              <div class="s-col s-col-12 main-content">
                <s-snackbar-container
                  :snackbars-data-arr="snackbars"
                  :position="'top'"
                  :width="20"
                  @pop-from-queue="removeSnackbar"
                />
                <router-view />
              </div>
            </div>
          </main>
        </template>
      </app-layout>
    </div>
    <router-view v-else />
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  ref,
  watch,
  type Ref,
} from 'vue';
import { useRoute } from 'vue-router';
import { SSnackbarContainer } from '@simmons/components';
import type { ValueOf } from 'ts-essentials';

import AppLayout from '@/layout/AppLayout.vue';
import AppSidebar from '@/layout/AppSidebar.vue';
import BrandingHeader from '@/layout/components/branding-header';
import { RecommendedBrowserBanner } from '@/components';

import { useStore } from '@/store';
import useAppEventListeners from '@/composables/useAppEventListeners';
import { Views } from '@/router/router.config';
import { sidebarWidths } from '@/config/constants';
import { isDocumentRouteName } from '@/utils';

import {
  APP__SNACKBAR,
  getters,
} from './store/modules/app/getters';
import { APP__REMOVE_SNACKBAR } from './store/modules/app/mutations';

const route = useRoute();
const { useMutation, useGetter } = useStore();
useAppEventListeners();

const isSidebarHovering = ref(false);
const sidebarWidth: Ref<ValueOf<typeof sidebarWidths>> = ref(sidebarWidths.opened);

const snackbars = computed(() => (
  useGetter<ReturnType<typeof getters[typeof APP__SNACKBAR]>>(APP__SNACKBAR)
));

const isAuthPage = computed(() => {
  const { name: routeName } = route;

  return (!routeName || (routeName && routeName === Views.AUTHENTICATION));
});

/*
  The document view is chrome-less: Word gives the document the whole window,
  so the product navigation collapses and the page gutters go. The burger in
  the branding header still summons the rail for anyone who wants it.
*/
const isFullBleed = computed(() => isDocumentRouteName(route.name));

// Whatever the rail was set to before the document view collapsed it.
const railWidthBeforeFullBleed: Ref<ValueOf<typeof sidebarWidths>> = ref(sidebarWidths.opened);

watch(isFullBleed, (fullBleed, wasFullBleed) => {
  if (fullBleed) {
    if (!wasFullBleed) {
      /*
        `hovered` is a transient state, so a user who happens to be hovering
        the rail as they open a document shouldn't have it restored later.
      */
      railWidthBeforeFullBleed.value = sidebarWidth.value === sidebarWidths.closed
        ? sidebarWidths.closed
        : sidebarWidths.opened;
    }

    sidebarWidth.value = sidebarWidths.closed;
  } else if (wasFullBleed) {
    sidebarWidth.value = railWidthBeforeFullBleed.value;
  }
}, { immediate: true });

function handleSidebarOpen(opened: boolean): void {
  sidebarWidth.value = opened ? sidebarWidths.opened : sidebarWidths.closed;
}

function handleToggleWidth(): void {
  if (isFullBleed.value) return;

  if (sidebarWidth.value !== sidebarWidths.closed) {
    isSidebarHovering.value = !isSidebarHovering.value;
    sidebarWidth.value = isSidebarHovering.value ? sidebarWidths.hovered : sidebarWidths.opened;
  }
}

function removeSnackbar(id: string): void {
  useMutation(APP__REMOVE_SNACKBAR, id);
}
</script>

<style lang="scss" scoped>
.app-container {
  position: relative;
  min-height: 100vh;

  display: flex;
  flex-direction: column;
}

.content {
  position: relative;
  flex: 1;
}
</style>

<style lang="scss">
@import "assets/styles/main.scss";
@import 'vue-json-pretty/lib/styles.css';

.main {
  width: 100%;
  height: 100%;
}

.main-content {
  width: 100%;
  margin-left: v-bind(sidebarWidth);
  padding-left: 20px;
  transition: margin-left 0.5s ease;
}

/*
  Full-bleed routes (the document view) hand the window to the view itself.

  Everything here is a flex chain so the view fills the remaining height
  without knowing what is above it — the browser-support banner can appear
  and disappear and nothing needs recalculating. The single exception is the
  branding banner, which is absolutely positioned and therefore invisible to
  layout; `--app-banner-height` comes from BrandingHeader.styles.scss.
*/
.app-container--full-bleed {
  height: 100vh;
  height: 100dvh;
  overflow: hidden;

  .content {
    min-height: 0;
  }

  /*
    No banner offset is needed: despite `.banner { position: absolute }`, the
    component library puts a `.container` class on the same element whose
    `position: relative` wins, so the banner sits in normal flow and the flex
    chain already accounts for its height.
  */
  .main {
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .s-row {
    flex: 1;
    min-height: 0;
  }

  /*
    `margin-left` is left alone: it still tracks the sidebar width so the
    view shifts if the rail is summoned.
  */
  /*
    `.s-row` is a ROW flex container, so `flex: 1` here governs width, not
    height — and because it also wraps, the flex line sizes to content, which
    let the editor grow to tens of thousands of pixels. `height: 100%` against
    the row's definite height is what actually constrains it.
  */
  .s-col.main-content {
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    min-height: 0;
    margin-top: 0;
    margin-right: 0;
    margin-bottom: 0;
    padding: 0;
  }
}

// TODO: update styles for dark mode in component lib
.s-menu__item {
  padding: 0 !important;
  margin-bottom: 0 !important;
}

.s-menu__item-content {
  width: 100%;
}

.white-link,
.white-link:link,
.white-link:visited {
  display: flex;
  color: rgb(var(--colour-white));
  padding: 1rem 2rem;
}

.white-link:hover,
.white-link:active,
.s-menu__item:hover .white-link,
.s-menu__item:active .white-link {
  color: rgb(var(--colour-primary));
}

.menu {
  transition: width 0.5s ease;

  li {
    font-size: 18px;
    align-items: center;

    .icon {
      margin-top: 5px;
    }
  }

  &Item {
    margin-left: -300px;
    transition: margin-left 0.5s ease;
  }
}

.menu:hover {
  .menuItem {
    margin-left: -20px;
  }
}
</style>
